/**
 * Готовые пакеты вопросов.
 *
 * Пакет — заранее собранный набор вопросов ниши: его можно взять целиком или
 * частями. Какие пакеты подходят бренду, решает сервер по внутренней метке
 * разбора (`vertical_code`): она уезжает параметром запроса и на экране не
 * показывается ни в каком виде — клиент её не выбирает и знать о ней не должен.
 *
 * Берётся пакет тем же путём, что и придуманные кандидаты, — через отбор и
 * `POST /questions/bulk` с `pack_id`. Ядро меняется одним явным действием, и
 * ревизия заводится одна.
 */

import { useState } from 'react';

import { api } from '../../api/client';
import { useLoad } from '../../ui/load';
import { Empty, ErrorNotice, Skeleton } from '../../ui/state';
import { Candidates } from './Candidates';
import { countOf, int } from '@shared/src/format';
import type { components } from '../../api/schema';

type PackSummary = components['schemas']['QuestionPackSummary'];
type Pack = components['schemas']['QuestionPack'];
type BulkResult = components['schemas']['QuestionBulkResult'];

export function Packs({
  brandId,
  verticalCode,
  free,
  onDone,
  onCancel,
}: {
  brandId: string;
  /** Внутренняя метка бренда: ею сервер отбирает пакеты. Не показывается. */
  verticalCode: string;
  free: number | null;
  onDone: (result: BulkResult) => void;
  onCancel: () => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useLoad<PackSummary[]>(`packs:${verticalCode}`, (signal) =>
    api.get<PackSummary[]>('/question-packs', {
      signal,
      query: { vertical_code: verticalCode },
    }),
  );
  // Бренд передаётся не для доступа, а ради текста вопросов: пакет лежит в общем
  // справочнике и написан подстановками — город и предмет продажи у каждого
  // бренда свои. Без `brand_id` сервер отдаст только те позиции, которым бренд
  // не нужен, и пакет придёт беднее, чем есть.
  const pack = useLoad<Pack | null>(`pack:${openId ?? 'none'}:${brandId}`, (signal) =>
    openId
      ? api.get<Pack>(`/question-packs/${openId}`, {
          signal,
          query: { brand_id: brandId },
        })
      : Promise.resolve(null),
  );

  if (openId) {
    if (pack.state.kind === 'loading') return <Skeleton rows={3} />;
    if (pack.state.kind === 'failed') {
      return (
        <ErrorNotice
          error={pack.state.error}
          action={
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={() => setOpenId(null)}
            >
              К списку пакетов
            </button>
          }
        />
      );
    }
    const opened = pack.state.data;
    if (opened) {
      // Карточка пакета обещает столько вопросов, сколько в нём написано, а
      // дошло до бренда столько, сколько удалось развернуть: вопрос про город
      // не задать бренду без города. Молчаливая разница читается как пропажа,
      // поэтому она названа словом и вместе с причиной.
      const promised =
        list.state.kind === 'ready'
          ? list.state.data.find((item) => item.id === opened.id)?.item_count
          : undefined;
      const lost = promised === undefined ? 0 : promised - opened.item_count;
      const note =
        lost > 0
          ? `${opened.description} В пакете ${countOf(opened.item_count, 'вопрос', 'вопроса', 'вопросов')} из ${int(promised ?? 0)}: остальные спрашивают про город и предмет продажи, а они у бренда не заполнены.`
          : opened.description;
      return (
        <div className="panel">
          <h2 className="panel-h">{opened.title}</h2>
          <Candidates
            brandId={brandId}
            candidates={opened.items ?? []}
            free={free}
            packId={opened.id}
            reason={`Готовый пакет: ${opened.title}`}
            note={note}
            onDone={onDone}
            onCancel={() => setOpenId(null)}
          />
        </div>
      );
    }
  }

  if (list.state.kind === 'loading') return <Skeleton rows={2} />;
  if (list.state.kind === 'failed') {
    return (
      <ErrorNotice
        error={list.state.error}
        action={
          <button className="btn btn-ghost btn-sm" type="button" onClick={list.reload}>
            Повторить
          </button>
        }
      />
    );
  }

  const packs = list.state.data;

  return (
    <div className="panel">
      <h2 className="panel-h">Готовые пакеты вопросов</h2>
      {packs.length === 0 ? (
        <Empty
          eyebrow="Готовые пакеты"
          title="Готовых пакетов для этого бренда пока нет"
          action={
            <button className="btn btn-ghost" type="button" onClick={onCancel}>
              Вернуться к ядру
            </button>
          }
        >
          Пакеты собираются заранее и подходят не всякому бизнесу. Пока пакета нет,
          вопросы можно придумать по вашему сайту — это тот же путь, только с учётом вашей
          специфики.
        </Empty>
      ) : (
        <>
          <ul className="finds">
            {packs.map((item) => (
              <li key={item.id}>
                <b>{item.title}</b>
                <span className="stamp">
                  {countOf(item.item_count, 'вопрос', 'вопроса', 'вопросов')}
                  {item.description ? ` · ${item.description}` : ''}
                </span>
                <button
                  type="button"
                  className="linkish"
                  onClick={() => setOpenId(item.id)}
                >
                  Посмотреть состав
                </button>
              </li>
            ))}
          </ul>
          <div className="row-actions">
            <button className="btn btn-ghost" type="button" onClick={onCancel}>
              Вернуться к ядру
            </button>
          </div>
        </>
      )}
    </div>
  );
}
